import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  UnitRow,
  UnitInfoCell,
  UnitImage,
  UnitName,
  UnitId,
  StatusBadge,
  StatusDot,
} from '../../../styles/UnitsMappingStyles';
import { UnitData, ChannelName, UnitStatus } from '../../../types';
import { T_KEYS } from '../../../constants';

// Custom Radio Button to match Figma design
const CustomRadio = styled('div')<{ $checked: boolean; $disabled: boolean }>(({ $checked, $disabled }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: $disabled ? 'not-allowed' : 'pointer',
  backgroundColor: $checked ? '#5b3fff' : 'transparent',
  border: $checked ? 'none' : '1.5px solid #bfbfbf',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: $disabled ? '#bfbfbf' : '#5b3fff',
  },
}));

const RadioInnerDot = styled('div')({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'white',
});

interface UnitRowItemProps {
  unit: UnitData;
  channelKey: ChannelName;
  isSelected: boolean;
  isMapped: boolean;
  onToggle: (unit: UnitData, channelKey: ChannelName) => void;
}

const UnitRowItem: React.FC<UnitRowItemProps> = memo(({
  unit,
  channelKey,
  isSelected,
  isMapped,
  onToggle,
}) => {
  const { t } = useTranslation();
  const unitStatus: UnitStatus = isMapped ? 'synced' : 'not_synced';

  return (
    <UnitRow $isSelected={isSelected} $isMapped={isMapped}>
      {/* Radio Column */}
      <Box sx={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
        <CustomRadio
          $checked={isSelected}
          $disabled={isMapped}
          onClick={() => !isMapped && onToggle(unit, channelKey)}
        >
          {isSelected && <RadioInnerDot />}
        </CustomRadio>
      </Box>

      {/* Property Column */}
      <UnitInfoCell sx={{ flex: 1 }}>
        <UnitImage
          src={unit.listingImage ?? undefined}
          alt={unit.listingName ?? ''}
          variant="rounded"
        >
          {unit.listingName?.charAt(0) || '?'}
        </UnitImage>
        <UnitName>{unit.listingName}</UnitName>
      </UnitInfoCell>


      {/* ID Column */}
      <Box sx={{ width: '120px' }}>
        <UnitId>{unit.channelListingId}</UnitId>
      </Box>

      {/* Status Column */}
      <Box sx={{ width: '100px', display: 'flex', justifyContent: 'center' }}>
        <StatusBadge $status={unitStatus}>
          <StatusDot $status={unitStatus} />
          {isMapped
            ? t(T_KEYS.UNITS_MAPPING_STATUS_MAPPED)
            : t(T_KEYS.UNITS_MAPPING_STATUS_NOT_MAPPED)
          }
        </StatusBadge>
      </Box>
    </UnitRow>
  );
});

UnitRowItem.displayName = 'UnitRowItem';

export default UnitRowItem;
